// Every user-facing string in the web app lives here (spec: one strings.ts for later i18n).
// UK English. Status/enum labels shared with mobile come from @walkkit/ui and are re-exported.
import {
  activityLabel,
  bookingLabel,
  healthLabel,
  invoiceLabel,
  moodLabel,
} from '@walkkit/ui';
import { formatPence } from '@walkkit/shared';
import type {
  InvoicePaidVia,
  BillingDay,
  BillingMode,
  BusinessDocumentKind,
  ClientStatus,
  ConsentKind,
  ConsentStatus,
  CreditReason,
  DogSex,
  IncidentType,
  InspectionSection,
  JobStatus,
  MandateStatus,
  MemberRole,
  RequestKind,
  RequestStatus,
  Role,
  ServiceKind,
  WalkGroup,
} from '@walkkit/shared';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const labels = {
  activity: activityLabel,
  booking: bookingLabel,
  health: healthLabel,
  invoice: invoiceLabel,
  mood: moodLabel,
  clientStatus: { enquiry: 'Enquiry', active: 'Active', paused: 'Paused', left: 'Left' } satisfies Record<ClientStatus, string>,
  walkGroup: { solo: 'Solo only', small: 'Small groups', any: 'Any group' } satisfies Record<WalkGroup, string>,
  sex: { male: 'Male', female: 'Female' } satisfies Record<DogSex, string>,
  serviceKind: {
    group_walk: 'Group walk',
    solo_walk: 'Solo walk',
    puppy_visit: 'Puppy visit',
    boarding_night: 'Boarding night',
    daycare_day: 'Daycare day',
  } satisfies Record<ServiceKind, string>,
  incidentType: {
    injury: 'Injury',
    fight: 'Fight',
    escape: 'Escape',
    bite: 'Bite',
    illness: 'Illness',
    property: 'Property damage',
    other: 'Other',
  } satisfies Record<IncidentType, string>,
  consentKind: {
    terms: 'Terms & cancellation policy',
    emergency_vet: 'Emergency vet treatment',
    off_lead: 'Off-lead consent',
    photo: 'Photo & social media',
    key_holding: 'Key holding',
  } satisfies Record<ConsentKind, string>,
  consentStatus: { pending: 'Awaiting signature', signed: 'Signed', revoked: 'Revoked' } satisfies Record<ConsentStatus, string>,
  mandateStatus: { pending: 'Pending', active: 'Active', inactive: 'Inactive', none: 'No mandate' } satisfies Record<
    MandateStatus | 'none',
    string
  >,
  creditReason: { holiday_skip: 'Holiday', business_cancel: 'Cancelled by us', goodwill: 'Goodwill' } satisfies Record<
    CreditReason,
    string
  >,
  role: { owner_admin: 'Owner', staff: 'Staff', client: 'Client' } satisfies Record<Role, string>,
  memberRole: { owner_admin: 'Owner (full access)', staff: 'Staff (no billing)' } satisfies Record<MemberRole, string>,
  requestKind: { holiday: 'Holiday', extra_walk: 'Extra walk' } satisfies Record<RequestKind, string>,
  requestStatus: { pending: 'Pending', approved: 'Approved', declined: 'Declined' } satisfies Record<RequestStatus, string>,
  jobStatus: { queued: 'Queued', running: 'Generating…', done: 'Ready', failed: 'Failed' } satisfies Record<JobStatus, string>,
  billingMode: { arrears: 'In arrears (after the walks)', advance: 'In advance (before the walks)' } satisfies Record<
    BillingMode,
    string
  >,
  billingDay: { first: '1st of the month', last_working_day: 'Last working day of the month' } satisfies Record<
    BillingDay,
    string
  >,
  documentKind: { insurance: 'Insurance certificate', licence: 'Licence certificate', other: 'Other document' } satisfies Record<
    BusinessDocumentKind,
    string
  >,
  inspectionSection: {
    register: 'Daily register of dogs in care',
    ratios: 'Staff-to-dog ratio per outing',
    owners: 'Owner, emergency contact and vet details',
    health: 'Vaccination, flea and worm status',
    consents: 'Signed consents',
    incidents: 'Incident and accident log',
    cleaning: 'Cleaning and disinfection log',
    staff: 'Staff first-aid certificates and DBS checks',
    documents: 'Insurance and licence certificates',
    medication: 'Medication administration log',
  } satisfies Record<InspectionSection, string>,
  level: { green: 'All good', amber: 'Needs attention', red: 'Action needed' },
  weekdayLong: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  slot: { am: 'Morning', midday: 'Midday', pm: 'Afternoon', custom: 'Specific time' },
};

export const s = {
  app: {
    name: 'WalkKit',
    tagline: 'Report cards, records and billing for dog walkers',
    poweredBy: 'Sent with WalkKit',
  },

  common: {
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    today: 'Today',
    yesterday: 'Yesterday',
    retry: 'Try again',
    loading: 'Loading…',
    loadMore: 'Load more',
    search: 'Search',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    optional: 'optional',
    required: 'Required',
    notSet: 'Not set',
    unknown: 'Unknown',
    confirm: 'Confirm',
    areYouSure: 'Are you sure?',
    view: 'View',
    open: 'Open',
    download: 'Download',
    copyLink: 'Copy link',
    copied: 'Link copied',
    change: 'Change',
    remove: 'Remove',
    upload: 'Upload',
    uploading: 'Uploading…',
    replace: 'Replace',
    choose: 'Choose…',
    anyone: 'Anyone',
    unassigned: 'Unassigned',
    from: 'From',
    to: 'To',
    date: 'Date',
    notes: 'Notes',
    status: 'Status',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    name: 'Name',
    actions: 'Actions',
    filters: 'Filters',
    clearFilters: 'Clear filters',
    ownerOnly: 'Only the business owner can see this.',
    ownerOnlyTitle: 'Owner access only',
    skipToContent: 'Skip to content',
    notFoundTitle: 'Page not found',
    notFoundBody: 'That page doesn’t exist or has moved.',
    goHome: 'Go to dashboard',
    errorTitle: 'Couldn’t load this',
    errorBody: 'Check your connection and try again.',
    count: plural,
  },

  errors: {
    network: 'You appear to be offline',
    generic: 'Something went wrong',
    unauthorised: 'Please sign in again',
    forbidden: 'You don’t have access to that',
    notFound: 'Not found',
    conflict: 'That conflicts with something else',
    validation: 'Some details need fixing',
    rateLimited: 'Too many requests – try again shortly',
    server: 'Something went wrong on our side',
    requestFailed: 'Request failed',
    unsupportedFile: (type: string) => `Unsupported file type: ${type}`,
    uploadFailed: 'Upload failed',
    uploadRejected: 'The upload was rejected',
    uploadTicketMissing: 'Upload could not be prepared',
    sessionExpired: 'Your session has ended. Please sign in again.',
  },

  auth: {
    signInTitle: 'Sign in to WalkKit',
    signInSubtitle: 'Welcome back. Let’s get the dogs out.',
    email: 'Email address',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    noAccount: 'New to WalkKit?',
    createAccount: 'Create an account',
    haveAccount: 'Already have an account?',
    emailLink: 'Email me a sign-in link instead',
    registerTitle: 'Start your WalkKit account',
    registerSubtitle: 'Set up your business in under a minute.',
    yourName: 'Your name',
    businessName: 'Business name',
    businessNameHint: 'As your clients know you, e.g. “Bob’s Barkers”.',
    passwordHint: 'At least 8 characters.',
    register: 'Create account',
    registering: 'Creating your account…',
    magicTitle: 'Sign in with an email link',
    magicSubtitle: 'We’ll email you a link that signs you straight in. It also has a 6-digit code if you’d rather type it.',
    sendLink: 'Email me a link',
    sending: 'Sending…',
    magicSentTitle: 'Check your email',
    magicSentBody: (email: string) => `We’ve sent a sign-in link to ${email}. It expires in 15 minutes.`,
    orEnterCode: 'Or enter the 6-digit code from the email',
    code: 'Code',
    codeInvalid: 'Enter the 6-digit code from the email',
    verifyCode: 'Sign in with code',
    usePassword: 'Sign in with a password',
    verifyingTitle: 'Signing you in…',
    verifyFailedTitle: 'That link didn’t work',
    verifyFailedBody: 'Sign-in links expire after 15 minutes and can only be used once. Request a new one below.',
    missingToken: 'This link is missing its token.',
    requestNewLink: 'Request a new link',
    signOut: 'Sign out',
    switchBusiness: 'Switch business',
    signedInAs: 'Signed in as',
    clientTitle: 'This dashboard is for dog walkers',
    clientBody: (name: string) =>
      `Hi ${name}. Your report cards, invoices and forms are all in the WalkKit app or in the links your walker sends you.`,
    clientOpenOwnerView: 'View my dogs’ cards and invoices',
    noBusiness: 'Your account isn’t linked to a business yet.',
  },

  nav: {
    main: 'Main navigation',
    home: 'Today',
    schedule: 'Schedule',
    clients: 'Clients',
    dogs: 'Dogs',
    cards: 'Report cards',
    records: 'Records pack',
    compliance: 'Compliance',
    incidents: 'Incidents',
    cleaning: 'Cleaning log',
    register: 'Daily register',
    consents: 'Consents',
    inspection: 'Inspection pack',
    billing: 'Billing',
    billingOverview: 'Overview',
    invoices: 'Invoices',
    credits: 'Credits',
    settings: 'Settings',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    userMenu: 'Account menu',
  },

  home: {
    setupPaymentsTitle: 'Set up payments',
    setupPaymentsBody: 'Connect Stripe so clients can pay you by Direct Debit, card, Apple Pay or Google Pay.',
    setupPaymentsCta: 'Set up payments',
    greeting: (name: string, hour: number) =>
      `${hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'}, ${name.split(' ')[0]}`,
    walksToday: 'Walks today',
    dogsToday: 'Dogs today',
    cardsSent: 'Cards sent',
    cardsOpened: 'Opened',
    cardsUnopened: 'Not opened yet',
    drafts: 'Drafts',
    draftCount: (n: number) => plural(n, 'draft', 'drafts'),
    unopenedCount: (n: number) => `${n} not opened yet`,
    todayWalks: 'Today’s walks',
    noWalksTitle: 'No walks today',
    noWalksBody: 'Set up weekly schedules and bookings appear here automatically.',
    openSchedule: 'Open schedule',
    ratio: (n: number, max: number) => `${n}/${max} dogs`,
    compliance: 'Compliance',
    viewAll: 'View all',
    failedPayments: 'Failed payments',
    noFailedPayments: 'No failed payments. Lovely.',
    requests: 'Client requests',
    noRequests: 'No requests waiting.',
    approve: 'Approve',
    decline: 'Decline',
    approved: 'Request approved',
    declined: 'Request declined',
    requestHoliday: (from: string, to: string) => `Holiday ${from} – ${to}`,
    requestExtra: (date: string, slot: string) => `Extra walk ${date}, ${slot}`,
    walkWith: (member: string | null) => (member ? `with ${member}` : 'Unassigned'),
    inProgress: 'In progress',
    cardSent: 'Card sent',
    vaccineFlag: 'Vaccination',
  },

  clients: {
    title: 'Clients',
    subtitle: 'The people whose dogs you look after.',
    add: 'Add client',
    searchPlaceholder: 'Search by name, email or phone',
    emptyTitle: 'No clients yet',
    emptyBody: 'Add your first client, then their dogs. You can invite them to the app afterwards.',
    noMatches: 'No clients match your filters.',
    status: 'Status',
    dogs: 'Dogs',
    contact: 'Contact',
    claimed: 'Using the app',
    notClaimed: 'Not invited yet',
    newTitle: 'New client',
    editTitle: 'Edit client',
    name: 'Full name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    emergencyName: 'Emergency contact name',
    emergencyPhone: 'Emergency contact phone',
    notes: 'Notes',
    notesHint: 'Key safe codes, gate quirks, parking… Only your team sees these.',
    created: 'Client added',
    updated: 'Client updated',
    tabs: { overview: 'Overview', dogs: 'Dogs', consents: 'Consents', billing: 'Billing' },
    invite: 'Invite to app',
    reinvite: 'Resend invite',
    invited: 'Invite sent',
    inviteNeedsEmail: 'Add an email address to invite this client.',
    emergencyContact: 'Emergency contact',
    since: (d: string) => `Client since ${d}`,
    noDogsTitle: 'No dogs yet',
    noDogsBody: 'Add this client’s dogs to schedule walks and send report cards.',
    addDog: 'Add dog',
    consentStatus: 'Consent forms',
    requestForms: 'Request forms',
    requestFormsTitle: 'Send consent forms',
    requestFormsBody: 'The client gets one email with a link to sign each form on their phone.',
    requestFormsSend: 'Send forms',
    formsSent: 'Consent forms sent',
    noConsentsTitle: 'No consent forms yet',
    noConsentsBody: 'Send the standard forms so you’re covered for emergencies and off-lead walks.',
    resendLink: 'Resend link',
    linkResent: 'Link resent',
    signedOn: (d: string) => `Signed ${d}`,
    viewPdf: 'View PDF',
    missing: 'Not requested',
    delete: 'Delete client',
    deleteConfirm: 'Delete this client? Their dogs, history and invoices are kept for your records, but they’ll disappear from lists.',
    deleted: 'Client deleted',
  },

  clientBilling: {
    mandate: 'Direct Debit',
    mandateActive: (ref: string | null) => (ref ? `Active · ref ${ref}` : 'Active'),
    mandateNone: 'No Direct Debit set up',
    mandateNoneBody: 'Send a secure Stripe link so they can set it up in two minutes.',
    sendMandateLink: 'Send mandate link',
    mandateLinkSent: 'Mandate link sent',
    nextCharge: 'Next charge',
    estimated: 'estimated',
    on: (d: string) => `on ${d}`,
    creditBalance: 'Credit balance',
    invoices: 'Invoices',
    noInvoices: 'No invoices yet. The first one is created on your next billing day.',
    credits: 'Credits',
    noCredits: 'No credits.',
    addHoliday: 'Add holiday',
    goodwill: 'Goodwill credit',
    applied: 'Applied',
    unapplied: 'Unapplied',
  },

  dogs: {
    title: 'Dogs',
    subtitle: 'Every dog in your care, with health status at a glance.',
    add: 'Add dog',
    searchPlaceholder: 'Search by dog, breed or owner',
    emptyTitle: 'No dogs yet',
    emptyBody: 'Add a client first, then add their dogs here.',
    noMatches: 'No dogs match your filters.',
    showInactive: 'Show inactive',
    owner: 'Owner',
    breed: 'Breed',
    health: 'Health',
    group: 'Walk group',
    newTitle: 'New dog',
    editTitle: 'Edit dog',
    name: 'Name',
    client: 'Owner',
    dob: 'Date of birth',
    sex: 'Sex',
    neutered: 'Neutered / spayed',
    weight: 'Weight (kg)',
    microchip: 'Microchip number',
    vetName: 'Vet practice',
    vetPhone: 'Vet phone',
    vetAddress: 'Vet address',
    medical: 'Medical notes',
    behaviour: 'Behaviour notes',
    active: 'Active',
    created: 'Dog added',
    updated: 'Dog updated',
    photo: 'Photo',
    changePhoto: 'Change photo',
    addPhoto: 'Add photo',
    photoUpdated: 'Photo updated',
    sections: {
      about: 'About',
      vet: 'Vet',
      notes: 'Medical & behaviour',
      health: 'Health records',
      schedule: 'Weekly schedule',
      cards: 'Recent report cards',
    },
    age: (years: number, months: number) =>
      years > 0 ? `${years} yr${years === 1 ? '' : 's'}${months ? ` ${months} mo` : ''}` : `${months} mo`,
    noSchedules: 'No regular walks. Add them from the schedule grid.',
    noCards: 'No report cards yet.',
    inactiveBadge: 'Inactive',
    coreVaccine: 'Core vaccine',
  },

  health: {
    add: 'Add health record',
    addTitle: 'Add health record',
    type: 'Type',
    label: 'Label',
    labelHint: 'Optional, e.g. “DHPPi + L4”',
    givenOn: 'Given on',
    expiresOn: 'Expires / due',
    evidence: 'Evidence (photo of vaccination card)',
    verify: 'Verify',
    verified: 'Verified',
    pending: 'Awaiting your check',
    emptyTitle: 'No health records',
    emptyBody: 'Add vaccinations, flea and worming so the owner is nudged before anything lapses.',
    added: 'Health record added',
    removed: 'Health record deleted',
    verifiedToast: 'Marked as verified',
    deleteConfirm: 'Delete this health record?',
    viewEvidence: 'View evidence',
  },

  schedule: {
    title: 'Schedule',
    subtitle: 'Each dog’s regular week. Drag a walk to another day to move it.',
    thisWeek: 'This week',
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
    weekOf: (d: string) => `Week of ${d}`,
    dog: 'Dog',
    addHoliday: 'Add holiday',
    emptyTitle: 'No dogs to schedule',
    emptyBody: 'Add active dogs and they’ll appear as rows here.',
    dragHint: 'Tip: drag a walk to move it to another day. Hold ⌥ Alt / Ctrl while dropping to copy it.',
    addToCell: (dog: string, day: string) => `Add a walk for ${dog} on ${day}`,
    projected: 'Not yet booked',
    endsOn: (d: string) => `Ends ${d}`,
    startsOn: (d: string) => `Starts ${d}`,
    adHoc: 'One-off',
    newTitle: (dog: string, day: string) => `${dog} · ${day}`,
    mode: 'Type',
    modeRecurring: 'Every week',
    modeOnce: 'Just this once',
    service: 'Service',
    slot: 'Time',
    customTime: 'Time (HH:MM)',
    member: 'Walker',
    startDate: 'Starts on',
    endDate: 'Ends on',
    endDateHint: 'Leave empty to repeat until you end it.',
    price: 'Price',
    priceHint: (p: string) => `Leave blank to use the service price (${p}).`,
    create: 'Add walk',
    created: 'Walk added',
    moved: 'Walk moved',
    copied: 'Walk copied',
    updated: 'Walk updated',
    detailTitle: 'Regular walk',
    bookingTitle: 'Booking',
    weekday: 'Day',
    end: 'End this schedule',
    endTitle: 'End schedule',
    endBody: 'Future planned walks after this date are removed. Past walks are never touched.',
    endLastDate: 'Last walk on',
    ended: 'Schedule ended',
    deleteSchedule: 'Delete schedule',
    deleteConfirm: 'Delete this schedule entirely? Future planned walks are removed; use “End” instead to keep its history.',
    deleted: 'Schedule deleted',
    bookingStatus: 'Status',
    markStatus: 'Change status',
    statusUpdated: 'Booking updated',
    reason: 'Reason',
    reasonHint: 'Needed when marking done without a report card.',
    cancelWalk: 'Cancel this walk',
    noServices: 'Add a service in Settings first.',
    fromSchedule: 'From a regular schedule',
    price_: (p: string) => p,
    cannotMoveAcrossDogs: 'Walks can only move between days for the same dog.',
    dropHere: 'Drop here',
    legend: 'Status key',
    totalWeek: (walks: number, total: string) => `${walks} walks · ${total}`,
  },

  holiday: {
    title: 'Add holiday',
    who: 'Who’s away?',
    closure: 'Business closure (everyone)',
    client: 'Client',
    dog: 'Dog',
    allDogs: 'All their dogs',
    start: 'First day',
    end: 'Last day',
    note: 'Note',
    preview: 'Preview',
    previewing: 'Checking…',
    affected: (n: number) => plural(n, 'walk affected', 'walks affected'),
    notCharged: 'Not charged',
    credited: 'Credited (already billed)',
    noneAffected: 'No planned walks fall in these dates.',
    confirm: 'Add holiday',
    created: (n: number, pounds: string) => `Holiday added – ${plural(n, 'walk', 'walks')} skipped, ${pounds} not charged`,
    billed: 'Already billed',
  },

  cards: {
    title: 'Report cards',
    subtitle: 'Every card sent, and whether the owner has opened it.',
    day: 'Day',
    prevDay: 'Previous day',
    nextDay: 'Next day',
    dog: 'Dog',
    client: 'Client',
    allDogs: 'All dogs',
    allClients: 'All clients',
    opened: 'Opened',
    unopened: 'Not opened',
    draft: 'Draft',
    emptyTitle: 'No cards for this day',
    emptyBody: 'Cards appear here as walkers send them from the app.',
    sentAt: (t: string) => `Sent ${t}`,
    openedAt: (t: string) => `Opened ${t}`,
    photos: (n: number) => plural(n, 'photo', 'photos'),
    replies: (n: number) => plural(n, 'reply', 'replies'),
    hearted: 'Hearted',
    resend: 'Resend to owner',
    resent: 'Card resent',
    openPublic: 'Open owner view',
    copyLink: 'Copy share link',
    revoke: 'Revoke link',
    revokeConfirm: 'Revoke this link? The owner’s old link stops working and a new one is created.',
    revoked: 'New link created',
    route: 'Route',
    noRoute: 'GPS was off for this walk.',
    messages: 'Messages from the owner',
    noMessages: 'No replies yet.',
    notes: 'Note',
    appended: 'Added later',
    activities: 'Activities',
    mood: 'Mood',
    medsDose: (dose: string) => `Dose: ${dose}`,
    walker: 'Walker',
    backToList: 'All cards',
    editableUntil: (t: string) => `Editable until ${t}`,
    locked: 'Locked – notes can still be appended',
    appendNote: 'Append a note',
    appendPlaceholder: 'Add something you forgot…',
    appendSubmit: 'Add note',
    noteAdded: 'Note added',
  },

  records: {
    complianceTitle: 'Compliance',
    complianceSubtitle: 'What an inspector would ask about, at a glance.',
    generatedAt: (t: string) => `Checked ${t}`,
    allClear: 'Nothing to action.',
    more: (n: number) => `+${n} more`,
    tileKeys: {
      vaccines: 'Vaccinations',
      consents: 'Consent forms',
      mandates: 'Direct Debits',
      staffCerts: 'Staff certificates',
      incidents: 'Incidents (30 days)',
      ratioBreaches: 'Ratio breaches',
    },
  },

  incidents: {
    title: 'Incidents',
    subtitle: 'Accidents and incidents, timestamped for your records.',
    log: 'Log incident',
    logTitle: 'Log an incident',
    emptyTitle: 'No incidents logged',
    emptyBody: 'Hopefully it stays that way. If something happens, log it here or from the app.',
    type: 'What happened?',
    dogs: 'Dogs involved',
    when: 'When',
    description: 'Description',
    descriptionHint: 'Stick to facts: what, where, who.',
    action: 'Action taken',
    vet: 'Vet contacted',
    photos: 'Photos',
    addPhotos: 'Add photos',
    notifyNow: 'Notify the owner now',
    logged: 'Incident logged',
    notify: 'Notify owner',
    notified: 'Owner notified',
    ownerNotified: (t: string) => `Owner notified ${t}`,
    ownerNotNotified: 'Owner not yet notified',
    reportedBy: 'Reported by',
    detailTitle: (type: string) => `${type} incident`,
    backToList: 'All incidents',
    updateAction: 'Update action taken',
    updated: 'Incident updated',
    noDogs: 'No specific dog',
  },

  cleaning: {
    title: 'Cleaning log',
    subtitle: 'Tick each area as it’s cleaned. Each tick is timestamped with who did it.',
    noAreasTitle: 'No cleaning areas set up',
    noAreasBody: 'Add the areas you clean (e.g. Van, Kennels, Kitchen) in Settings.',
    setupAreas: 'Set up areas',
    doneBy: (who: string, time: string) => `${who} · ${time}`,
    ticked: (area: string) => `${area} ticked`,
    unticked: (area: string) => `${area} unticked`,
    progress: (done: number, total: number) => `${done} of ${total} done`,
    note: 'Note (optional)',
  },

  register: {
    title: 'Daily register',
    subtitle: 'Dogs in your care each day, and the staff-to-dog ratio on every outing.',
    walks: 'Outings',
    dogs: 'Dogs in care',
    walker: 'Walker',
    time: 'Time',
    dogCount: 'Dogs',
    ratio: 'Ratio',
    breach: 'Ratio breach',
    arrival: 'Arrival',
    departure: 'Departure',
    service: 'Service',
    emptyTitle: 'No dogs in care on this day',
    emptyBody: 'The register is built automatically from walks and boarding.',
    generatedAt: (t: string) => `Generated ${t}`,
    noWalks: 'No outings recorded.',
  },

  consents: {
    title: 'Consents',
    subtitle: 'Your form templates, and who still needs to sign.',
    templates: 'Templates',
    status: 'Who’s signed',
    editTemplate: 'Edit template',
    newVersionHint: 'Saving creates a new version. Forms already signed keep the version they signed.',
    version: (n: number) => `v${n}`,
    titleField: 'Title',
    body: 'Form text',
    bodyHint: 'Plain text. Blank lines start a new paragraph.',
    fields: 'Fields the client fills in',
    addField: 'Add field',
    fieldKey: 'Key',
    fieldLabel: 'Label',
    fieldType: 'Type',
    fieldRequired: 'Required',
    fieldDefault: 'Default',
    fieldTypes: { checkbox: 'Tick box', text: 'Text', number: 'Number' },
    removeField: 'Remove field',
    templateSaved: (v: number) => `Saved as version ${v}`,
    noTemplate: 'Not set up yet',
    createTemplate: 'Create template',
    matrixEmpty: 'No active clients yet.',
    unsigned: (n: number) => `${n} unsigned`,
    fieldCount: (n: number) => plural(n, 'field', 'fields'),
    request: 'Request',
    clientCol: 'Client',
  },

  inspection: {
    title: 'Inspection pack',
    subtitle: 'One PDF for the council inspector, built from your records.',
    range: 'Date range',
    sections: 'Sections',
    appendix: 'Include appendix (consent PDFs and vaccination card images)',
    generate: 'Generate PDF',
    generating: 'Generating your pack…',
    generatingBody: 'Usually under a minute. You can leave this page; it’ll be in the history below.',
    ready: 'Your pack is ready',
    failed: 'Generation failed',
    downloadPdf: 'Download PDF',
    history: 'Previously generated',
    historyEmpty: 'No packs generated yet.',
    period: (from: string, to: string) => `${from} – ${to}`,
    selectAll: 'Select all',
    selectNone: 'Clear',
    took: (secs: number) => `took ${secs}s`,
    sectionCount: (n: number) => plural(n, 'section', 'sections'),
    linkExpired: 'Download links expire after a short time; refresh to get a new one.',
  },

  billing: {
    title: 'Billing',
    subtitle: 'Direct Debits, invoices and what’s coming in.',
    connectTitle: 'Connect Stripe to get paid',
    connectBody: 'Take Direct Debits and card payments, including Apple Pay and Google Pay, straight into your bank account. Setup takes about 10 minutes, and Stripe will ask for photo ID.',
    fees: 'Stripe’s standard UK fees come out of each payment. WalkKit adds no transaction fees.',
    feesLink: 'See Stripe’s fees',
    connectCta: 'Set up payments with Stripe',
    connecting: 'Opening Stripe…',
    connectedTitle: 'Payments connected',
    period: (from: string, to: string) => `${from} – ${to}`,
    runsOn: (d: string) => `Next billing run ${d}`,
    expected: 'Expected this period',
    byStatus: 'Invoices by status',
    failed: 'Failed payments',
    failedEmpty: 'No failed payments.',
    retry: 'Retry payment',
    retried: 'Payment resubmitted',
    pending: 'Pending Bacs payments',
    pendingEmpty: 'Nothing pending.',
    pendingHint: 'Bacs takes about 4 working days to confirm.',
    noMandate: 'Clients without a Direct Debit',
    noMandateEmpty: 'Everyone’s set up. 🎉',
    sendLink: 'Send link',
    linkSent: (name: string) => `Mandate link sent to ${name}`,
    clients: 'Clients',
    colClient: 'Client',
    colMandate: 'Direct Debit',
    colNext: 'Next charge',
    colCredit: 'Credit',
    colLast: 'Last invoice',
    runNow: 'Run billing now',
    runTitle: 'Run billing now',
    runBody: 'Creates invoices for every client with billable walks in the chosen month and submits Direct Debits. Running twice for the same month does nothing.',
    runMonth: 'Billing month',
    run: 'Run billing',
    running: 'Running…',
    runDone: (n: number, total: string) => `${plural(n, 'invoice', 'invoices')} created · ${total}`,
    alreadyRun: 'Billing has already been run for this period. Nothing was duplicated.',
    viewInvoices: 'View invoices',
  },

  invoices: {
    copyPayLink: 'Copy pay link',
    payLinkHint: 'A link your client can use to pay by card, Apple Pay or Google Pay',
    title: 'Invoices',
    subtitle: 'Issued invoices are final; corrections go on the next one as credits.',
    emptyTitle: 'No invoices yet',
    emptyBody: 'Invoices are created automatically on your billing day, or when you run billing manually.',
    number: 'Invoice',
    client: 'Client',
    period: 'Period',
    total: 'Total',
    status: 'Status',
    chargeDate: 'Charge date',
    allStatuses: 'All statuses',
    pdf: 'Download PDF',
    backToList: 'All invoices',
    lines: 'Lines',
    subtotal: 'Subtotal',
    credits: 'Credits',
    skipped: 'Skipped (not charged)',
    skippedDates: (dates: string) => `Skipped: ${dates}`,
    issued: (d: string) => `Issued ${d}`,
    paid: (d: string) => `Paid ${d}`,
    paidVia: {
      direct_debit: 'Direct Debit',
      pay_link: 'Paid online',
      cash: 'Cash',
      bank_transfer: 'Bank transfer',
      other: 'Other',
    } as Record<InvoicePaidVia, string>,
    markPaid: 'Mark as paid',
    markPaidTitle: 'Mark as paid',
    markPaidBody: 'Record a payment made outside WalkKit, such as cash or a bank transfer. Your client won’t be charged or reminded about this invoice.',
    markPaidMethod: 'How was it paid?',
    markPaidDate: 'Date paid',
    markPaidNote: 'Note',
    markPaidNoteHint: 'For example, “Cash at the door”',
    markPaidSave: 'Mark as paid',
    markedPaid: 'Invoice marked as paid',
    markUnpaid: 'Mark as unpaid',
    markUnpaidBody: 'This undoes “Mark as paid”. The invoice goes back to unpaid and the client can pay it online again.',
    markedUnpaid: 'Invoice marked as unpaid',
    failureReason: 'Why it failed',
    disputed: 'Disputed',
    disputedBody: 'The client has disputed this payment with their bank. Stripe will email you the next steps.',
    immutable: 'This invoice has been issued and can’t be changed. Add a credit to correct it on the next invoice.',
    qtyAt: (qty: number, unit: string) => `${qty} × ${unit}`,
    noPdf: 'The PDF is still being generated.',
  },

  credits: {
    title: 'Credits',
    subtitle: 'Money owed back to clients, applied automatically to their next invoice.',
    add: 'Add goodwill credit',
    addTitle: 'Goodwill credit',
    client: 'Client',
    amount: 'Amount (£)',
    reason: 'Reason',
    note: 'Note',
    notePlaceholder: 'e.g. Sorry for the late pickup on Tuesday',
    created: 'Credit added',
    emptyTitle: 'No credits',
    emptyBody: 'Holiday skips and cancellations add credits automatically when you bill in advance.',
    applied: 'Applied',
    unapplied: 'Waiting for next invoice',
    invalidAmount: 'Enter an amount like 12 or 12.50',
  },

  settings: {
    title: 'Settings',
    tabs: {
      business: 'Business',
      billing: 'Billing rules',
      services: 'Services & prices',
      team: 'Team',
      documents: 'Documents',
      cleaning: 'Cleaning areas',
    },
    saved: 'Settings saved',
    business: {
      heading: 'Business profile',
      body: 'Shown on report cards, consent forms and invoices.',
      name: 'Business name',
      brandColour: 'Brand colour',
      brandHint: 'Used on your report cards and forms.',
      logo: 'Logo',
      logoHint: 'Square PNG or JPG works best.',
      address: 'Address',
      phone: 'Phone',
      licenceNumber: 'Licence number',
      licenceExpiry: 'Licence expiry',
      maxDogs: 'Max dogs per walker',
      maxDogsHint: 'Walkers can’t add more dogs than this to one walk.',
      logoUpdated: 'Logo updated',
      preview: 'Preview',
    },
    billing: {
      heading: 'Billing rules',
      body: 'How and when clients are charged.',
      mode: 'When to bill',
      day: 'Billing day',
      cancelNotice: 'Cancellation notice (hours)',
      cancelNoticeHint: 'Client cancellations with less notice than this count as late.',
      lateBilled: 'Charge for late cancellations',
      noAccessBilled: 'Charge when the walker couldn’t get in',
      cardFallback: 'Let clients pay by card if they can’t use Direct Debit',
      reminders: 'Send payment reminders',
      remindersHint: 'Emails clients about unpaid invoices on the due date, then 3 and 7 days later. Never sent while a Direct Debit is collecting.',
    },
    services: {
      heading: 'Services & prices',
      body: 'Your price list. Prices are per walk, visit or night.',
      add: 'Add service',
      addTitle: 'New service',
      editTitle: 'Edit service',
      name: 'Name',
      kind: 'Kind',
      duration: 'Duration (minutes)',
      price: 'Price (£)',
      active: 'Available to book',
      applyToFuture: 'Apply the new price to walks already booked',
      applyToFutureHint: 'Otherwise it applies only to walks booked from now on.',
      emptyTitle: 'No services yet',
      emptyBody: 'Add what you offer, e.g. “Group walk – 1 hour – £12”.',
      inactive: 'Hidden',
      created: 'Service added',
      updated: 'Service updated',
      invalidPrice: 'Enter a price like 12 or 12.50',
      perUnit: (mins: number) => (mins ? `${mins} min` : ''),
    },
    team: {
      heading: 'Team',
      body: 'Everyone who walks for you. Certificates feed your records pack.',
      invite: 'Invite team member',
      inviteTitle: 'Invite team member',
      inviteBody: 'They’ll get an email with a link to sign in to the app.',
      editTitle: 'Edit team member',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      role: 'Role',
      firstAid: 'First-aid certificate expires',
      dbs: 'DBS check date',
      active: 'Active',
      deactivate: 'Deactivate',
      reactivate: 'Reactivate',
      deactivateConfirm: 'Deactivate this team member? They’ll be signed out of the app. Their history is kept.',
      invited: 'Invite sent',
      updated: 'Team member updated',
      inactive: 'Inactive',
      you: 'You',
      expiresOn: (d: string) => `First aid until ${d}`,
      noFirstAid: 'No first-aid cert',
      dbsOn: (d: string) => `DBS ${d}`,
    },
    documents: {
      heading: 'Business documents',
      body: 'Insurance and licence certificates for your inspection pack.',
      add: 'Upload document',
      addTitle: 'Upload document',
      kind: 'Type',
      label: 'Label',
      file: 'File (PDF or photo)',
      expires: 'Expires on',
      emptyTitle: 'No documents yet',
      emptyBody: 'Upload your public liability insurance and any licence certificates.',
      uploaded: 'Document uploaded',
      deleted: 'Document deleted',
      deleteConfirm: 'Delete this document?',
      expiresOn: (d: string) => `Expires ${d}`,
      noFile: 'Choose a file to upload.',
    },
    cleaning: {
      heading: 'Cleaning areas',
      body: 'The checklist shown in the cleaning log each day.',
      add: 'Add area',
      placeholder: 'e.g. Van cage',
      empty: 'No areas yet.',
      moveUp: 'Move up',
      moveDown: 'Move down',
    },
  },

  publicCard: {
    title: (dog: string) => `${dog}’s report card`,
    walkWith: (dog: string, walker: string | null) => (walker ? `${dog}’s walk with ${walker}` : `${dog}’s day`),
    photoOf: (dog: string, n: number) => `Photo ${n} of ${dog}`,
    gallery: 'Photos',
    openPhoto: (n: number) => `Open photo ${n}`,
    route: 'Route',
    distance: 'Distance',
    duration: 'Duration',
    minutes: (n: number) => `${n} min`,
    time: 'Time',
    mood: 'Mood',
    activities: 'Activities',
    meds: (dose: string | null) => (dose ? `Meds given · ${dose}` : 'Meds given'),
    note: (walker: string | null) => (walker ? `A note from ${walker}` : 'A note'),
    update: 'Update',
    heart: 'Send a heart',
    hearted: 'You sent a heart',
    replyLabel: 'Reply',
    replyPlaceholder: 'Say thanks or ask a question…',
    sendReply: 'Send',
    sending: 'Sending…',
    replySent: 'Reply sent',
    charsLeft: (n: number) => `${n} characters left`,
    replies: 'Messages',
    you: 'You',
    call: (business: string) => `Call ${business}`,
    notFoundTitle: 'This card isn’t available',
    notFoundBody: 'The link may have expired or been replaced. Ask your walker to send it again.',
    errorTitle: 'We couldn’t load this card',
    errorBody: 'Please check your connection and try again.',
    closeLightbox: 'Close photo',
    prevPhoto: 'Previous photo',
    nextPhoto: 'Next photo',
    lightboxLabel: 'Photo viewer',
    video: 'Video',
    start: 'Start',
    finish: 'Finish',
  },

  publicConsent: {
    title: (form: string) => form,
    forClient: 'Your details',
    dogs: 'Your dogs',
    vet: (vet: string) => `Vet: ${vet}`,
    readCarefully: 'Please read the form below.',
    fields: 'Your answers',
    signature: 'Signature',
    signatureHint: 'Sign with your finger or mouse in the box.',
    signatureRequired: 'Please sign in the box',
    clear: 'Clear',
    signerName: 'Your full name',
    submit: 'Sign and submit',
    submitting: 'Submitting…',
    agree: (d: string) => `By signing on ${d}, you agree to the form above.`,
    thanksTitle: 'Thank you – all signed',
    thanksBody: (business: string) => `${business} has your signed form. You can close this page.`,
    alreadySignedTitle: 'Already signed',
    alreadySignedBody: (d: string) => `This form was signed on ${d}. Nothing more to do.`,
    revokedTitle: 'This form is no longer needed',
    revokedBody: 'Your walker has withdrawn this form. If you think that’s wrong, get in touch with them.',
    notFoundTitle: 'This form isn’t available',
    notFoundBody: 'The link may have expired. Ask your walker to send it again.',
    errorTitle: 'We couldn’t load this form',
    fieldRequired: 'This is required',
    version: (v: number) => `Version ${v}`,
    signatureCanvas: 'Signature pad',
  },

  publicPay: {
    title: (number: string) => `Invoice ${number}`,
    invoiceFor: (number: string, client: string) => `Invoice ${number} · ${client}`,
    period: (from: string, to: string) => `${from} – ${to}`,
    total: 'Total',
    payCta: (pence: number) => `Pay ${formatPence(pence)}`,
    methods: 'Card, Apple Pay or Google Pay. Secure payment by Stripe.',
    failedNote: 'Your Direct Debit for this invoice didn’t go through. You can pay it here instead.',
    unavailable: (business: string, phone: string | null) =>
      `${business} can’t take online payments yet. Please get in touch with them to pay${phone ? ` (${phone})` : ''}.`,
    downloadPdf: 'Download invoice (PDF)',
    paidTitle: 'Paid – thank you',
    thanksTitle: 'Thank you – payment received',
    paidBody: (number: string, date: string | null) => `Invoice ${number} is paid${date ? ` (${date})` : ''}. Nothing more to do.`,
    confirmingTitle: 'Confirming your payment…',
    confirmingBody: 'This usually takes a few seconds. Please keep this page open.',
    processingTitle: 'Payment is being processed',
    processingBody: (business: string) => `Stripe will email you a receipt, and ${business} will see the payment shortly. You can close this page.`,
    directDebitTitle: 'Paid by Direct Debit',
    directDebitBody: (date: string) => `This invoice will be collected by Direct Debit on or shortly after ${date}. Nothing to do.`,
    notFoundTitle: 'This invoice isn’t available',
    notFoundBody: 'The link may be wrong or out of date. Ask your walker to send it again.',
    errorTitle: 'We couldn’t load this invoice',
  },

  mandateDone: {
    title: 'Direct Debit set up',
    body: 'Thank you. Your Direct Debit is set up and future invoices will be collected automatically. You’ll get an email with each invoice at least 3 working days before any payment is taken.',
    protected: 'Payments are protected by the Direct Debit Guarantee.',
    close: 'You can close this page.',
  },

  owner: {
    title: 'My dogs',
    payInvoice: 'Pay',
    feed: 'Report cards',
    billing: 'Invoices',
    forms: 'Forms',
    feedEmpty: 'No report cards yet. They’ll appear here after each walk.',
    billingEmpty: 'Nothing to pay yet.',
    formsEmpty: 'No forms to sign.',
    unread: 'New',
    sign: 'Sign now',
    signed: 'Signed',
    manageDd: 'Set up Direct Debit',
    ddActive: 'Direct Debit active',
    openCard: 'Open card',
    loadMore: 'Load more cards',
  },
};

export type Strings = typeof s;
